import type { Locale } from './locale-context';

const translations = {
  en: {
    // Quiz page
    shortQuizTitle: 'The short political quiz',
    longQuizTitle: 'The long political quiz',
    loadingQuestions: 'Loading quiz questions...',
    loadingMobile: 'This can take a moment on a phone.',
    failedToLoad: 'The quiz failed to load',
    tryAgain: 'Try again',
    persistError: 'If this problem persists, please try using a different browser or clearing your cache.',
    noQuestions: 'No questions available',
    reloadPage: 'Reload page',

    // Slider labels
    stronglyDisagree: 'Strongly Disagree',
    stronglyAgree: 'Strongly Agree',
    neutral: 'Neutral',
    slightlyDisagree: 'Slightly Disagree',
    disagree: 'Disagree',
    stronglyDisagreeLabel: 'Strongly Disagree',
    slightlyAgree: 'Slightly Agree',
    agree: 'Agree',
    stronglyAgreeLabel: 'Strongly Agree',

    // Buttons
    back: 'Back',
    next: 'Next',
    seeResults: 'See your results',
    skipQuestion: 'Skip question',
    unskipQuestion: 'Unskip question',
    showExplanation: 'Show explanation',
  },
  de: {
    // Quiz page
    shortQuizTitle: 'Das kurze Politik-Quiz',
    longQuizTitle: 'Das lange Politik-Quiz',
    loadingQuestions: 'Quizfragen werden geladen...',
    loadingMobile: 'Auf dem Handy kann das einen Moment dauern.',
    failedToLoad: 'Quiz konnte nicht geladen werden',
    tryAgain: 'Erneut versuchen',
    persistError: 'Wenn dieses Problem weiterhin besteht, versuchen Sie bitte einen anderen Browser oder leeren Sie Ihren Cache.',
    noQuestions: 'Keine Fragen verfügbar',
    reloadPage: 'Seite neu laden',

    // Slider labels
    stronglyDisagree: 'Stimme gar nicht zu',
    stronglyAgree: 'Stimme voll zu',
    neutral: 'Neutral',
    slightlyDisagree: 'Stimme eher nicht zu',
    disagree: 'Stimme nicht zu',
    stronglyDisagreeLabel: 'Stimme gar nicht zu',
    slightlyAgree: 'Stimme eher zu',
    agree: 'Stimme zu',
    stronglyAgreeLabel: 'Stimme voll zu',

    // Buttons
    back: 'Zurück',
    next: 'Weiter',
    seeResults: 'Ergebnisse ansehen',
    skipQuestion: 'Frage überspringen',
    unskipQuestion: 'Frage wiederherstellen',
    showExplanation: 'Erklärung anzeigen',
  },
} as const;

export type TranslationKey = keyof typeof translations.en;

export function t(locale: Locale, key: TranslationKey): string {
  return translations[locale][key] ?? translations.en[key];
}

export function getValueLabelTranslated(locale: Locale, value: number): string {
  const percentage = Math.abs((value - 0.5) * 200);
  if (percentage <= 10) return t(locale, 'neutral');

  if (value < 0.5) {
    if (percentage <= 35) return t(locale, 'slightlyDisagree');
    if (percentage <= 65) return t(locale, 'disagree');
    return t(locale, 'stronglyDisagreeLabel');
  } else {
    if (percentage <= 35) return t(locale, 'slightlyAgree');
    if (percentage <= 65) return t(locale, 'agree');
    return t(locale, 'stronglyAgreeLabel');
  }
}
